import { useEffect, useRef } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Grid } from 'components/layout';
import { useServiceFeatures } from 'hooks/useServiceContext';
import useTMSContext from 'hooks/useTMSContext';
import { NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { normalizeExternalTMSID } from 'utils/loadInfoAndBuilding';

import { RevenovaTenant } from './constants';
import { getTenantFromString } from './helpers';

const REVENOVA_BOL_REF_MAX_CHARS_DEFAULT = 55;
// Cooler has custom logic in the backend that maps the BOL num to
// Bil to Reference number, which has a char limit of 45.
const REVENOVA_BOL_REF_MAX_CHARS_COOLER = 100;
const REVENOVA_PO_NUM_MAX_CHARS = 100;

// Sentinel to distinguish "effect never ran" from "user cleared selection"
const UNINITIALIZED = Symbol('uninitialized');

export type CustomerSectionFormProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  isLoadingCustomers: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  originalSuggestionData?: NormalizedLoad;
  isCreateMode?: boolean;
};

export function CustomerSectionForm({
  formMethods,
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  originalSuggestionData,
  isCreateMode = false,
}: CustomerSectionFormProps) {
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;
  const { tmsIntegrations } = useServiceFeatures();
  const { tenant } = useTMSContext();
  const isCoolerTenant = tenant
    ? getTenantFromString(tenant) === RevenovaTenant.Cooler
    : false;
  const bolRefMaxChars = isCoolerTenant
    ? REVENOVA_BOL_REF_MAX_CHARS_COOLER
    : REVENOVA_BOL_REF_MAX_CHARS_DEFAULT;

  const watchedCustomer = watch('customer');
  const watchedAddress2 = watch('customer.addressLine2');
  const previousExternalTMSIDRef = useRef<string | typeof UNINITIALIZED>(
    UNINITIALIZED
  );
  // Track if we've hydrated with rich data (has addressLine1) for the current ID
  const hydratedWithRichDataRef = useRef<boolean>(false);

  // Hydrate customer fields when a NEW customer is selected (externalTMSID changes).
  // In create mode, also re-hydrate once if initial data was sparse and richer data arrives.
  useEffect(() => {
    const currentExternalTMSID = watchedCustomer?.externalTMSID;
    const normalizedCurrent = normalizeExternalTMSID(currentExternalTMSID);

    const isFirstRun = previousExternalTMSIDRef.current === UNINITIALIZED;
    const normalizedPrevious = isFirstRun
      ? ''
      : normalizeExternalTMSID(previousExternalTMSIDRef.current as string);

    if (!normalizedCurrent) {
      previousExternalTMSIDRef.current = currentExternalTMSID ?? '';
      hydratedWithRichDataRef.current = false;
      return;
    }

    const externalTMSIDChanged = normalizedPrevious !== normalizedCurrent;

    // Reset rich data flag when ID changes
    if (externalTMSIDChanged) {
      hydratedWithRichDataRef.current = false;
    }

    previousExternalTMSIDRef.current = currentExternalTMSID ?? '';

    // Skip initial hydrate in edit mode (form already has load data)
    // But DO hydrate if user cleared and re-selected (isFirstRun distinguishes these)
    if (isFirstRun && !isCreateMode) {
      return;
    }

    const selectedCustomer = customers?.find(
      (c) => normalizeExternalTMSID(c.externalTMSID) === normalizedCurrent
    );

    if (!selectedCustomer) {
      return;
    }

    // Determine if this customer record has rich data (address populated)
    const hasRichData = Boolean(selectedCustomer.addressLine1?.trim());

    // Hydrate if:
    // 1. ID just changed (user selected a new customer), OR
    // 2. In create mode, we haven't hydrated with rich data yet and now have it
    const shouldHydrate =
      externalTMSIDChanged ||
      (isCreateMode && hasRichData && !hydratedWithRichDataRef.current);

    if (shouldHydrate) {
      Object.entries(selectedCustomer).forEach(([key, value]) => {
        setValue(`customer.${key}` as FieldPath<NormalizedLoad>, value, {
          shouldDirty: true,
        });
      });

      if (hasRichData) {
        hydratedWithRichDataRef.current = true;
      }
    }
  }, [isCreateMode, watchedCustomer?.externalTMSID, customers, setValue]);

  // In edit mode, if externalTMSID is empty but we have customer data, create a virtual customer option
  useEffect(() => {
    if (
      !isCreateMode &&
      watchedCustomer &&
      !watchedCustomer.externalTMSID &&
      watchedCustomer.name
    ) {
      const virtualCustomer: TMSCustomer = {
        externalTMSID: '', // Empty but valid
        name: watchedCustomer.name,
        addressLine1: watchedCustomer.addressLine1 || '',
        addressLine2: watchedCustomer.addressLine2 || '',
        city: watchedCustomer.city || '',
        state: watchedCustomer.state || '',
        zipCode: watchedCustomer.zipCode || '',
        country: watchedCustomer.country || '',
        contact: watchedCustomer.contact || '',
        phone: watchedCustomer.phone || '',
        email: watchedCustomer.email || '',
        refNumber: watchedCustomer.refNumber || '',
      };

      // Add virtual customer to the list if it doesn't exist
      if (
        customers &&
        !customers.find(
          (c) => c.name === virtualCustomer.name && c.externalTMSID === ''
        )
      ) {
        setCustomers([virtualCustomer, ...customers]);
      }
    }
  }, [isCreateMode, watchedCustomer, customers, setCustomers]);

  return (
    <>
      <RHFCustomerSelect
        required={isCreateMode}
        name={`customer.externalTMSID`}
        label='Name'
        control={control}
        errors={errors}
        customers={customers}
        setCustomers={setCustomers}
        isLoadingCustomers={isLoadingCustomers}
        setIsLoadingCustomers={setIsLoadingCustomers}
        tmsID={tmsIntegrations?.[0]?.id}
        preserveSearchedSelection
      />

      <RHFTextInput
        name='customer.addressLine1'
        label='Address Line 1'
        readOnly={true}
      />

      {!!watchedAddress2 && (
        <RHFTextInput
          name='customer.addressLine2'
          label='Address Line 2'
          readOnly={true}
        />
      )}

      <RHFTextInput name='customer.city' label='City' readOnly={true} />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <RHFTextInput
          name='customer.state'
          label='State'
          aiIconOnly={true}
          readOnly={true}
        />
        <RHFTextInput
          name='customer.zipCode'
          label='Zip Code'
          placeholder='12345'
          aiIconOnly={true}
          readOnly={true}
        />
      </Grid>

      <LoadRefPillInput
        name='customer.refNumber'
        label='Ref #/ BOL'
        control={control}
        originalSuggestionData={originalSuggestionData || getValues()}
        autoSelectFirstOption={false}
        maxChars={bolRefMaxChars}
        rules={{
          validate: (value) => {
            const str = typeof value === 'string' ? value : '';
            if (str.length > bolRefMaxChars) {
              return `Ref #/ BOL must be ${bolRefMaxChars} characters or fewer (currently ${str.length})`;
            }
            return true;
          },
        }}
      />
      <LoadRefPillInput
        name='poNums'
        label='PO #'
        control={control}
        originalSuggestionData={originalSuggestionData}
        autoSelectFirstOption={false}
        maxChars={REVENOVA_PO_NUM_MAX_CHARS}
        rules={{
          validate: (value) => {
            const str = typeof value === 'string' ? value : '';
            if (str.length > REVENOVA_PO_NUM_MAX_CHARS) {
              return `PO # must be ${REVENOVA_PO_NUM_MAX_CHARS} characters or fewer (currently ${str.length})`;
            }
            return true;
          },
        }}
      />
    </>
  );
}
